import { githubUserGet } from '../api/github/userGet';

const TOKEN_KEY = 'github_token';
const REDIRECT_KEY = 'github_post_login_redirect';

export function loginWithGitHub(returnTo?: string): void {
  const clientId = import.meta.env.VITE_GITHUB_CLIENT_ID;
  if (!clientId) {
    throw new Error('GitHub client ID is not configured.');
  }

  const redirectTarget =
    returnTo ?? window.location.pathname + window.location.search + window.location.hash;
  sessionStorage.setItem(REDIRECT_KEY, redirectTarget);

  const redirectUri = `${window.location.origin}/callback`;
  const scope = 'repo';
  const authUrl = new URL('https://github.com/login/oauth/authorize');
  authUrl.searchParams.set('client_id', clientId);
  authUrl.searchParams.set('redirect_uri', redirectUri);
  authUrl.searchParams.set('scope', scope);
  authUrl.searchParams.set('state', encodeURIComponent(redirectTarget));

  window.location.assign(authUrl.toString());
}

export interface GitHubCallbackResult {
  token?: string;
  code?: string;
  error?: string;
}

export function handleGitHubCallback(): GitHubCallbackResult {
  const params = new URLSearchParams(window.location.search);
  const token = params.get('token') ?? params.get('access_token') ?? undefined;
  const code = params.get('code') ?? undefined;
  const error = params.get('error') ?? undefined;

  if (token) {
    setGitHubToken(token);
  }

  return { token, code, error };
}

export function getGitHubRedirectFromState(): string | null {
  const params = new URLSearchParams(window.location.search);
  const state = params.get('state');
  if (!state) {
    return null;
  }
  try {
    return decodeURIComponent(state);
  } catch {
    return state;
  }
}

export function getGitHubToken(): string | null {
  return sessionStorage.getItem(TOKEN_KEY);
}

export interface GitHubUser {
  login: string;
  name: string | null;
  avatar_url: string;
}

export async function getGitHubUser(): Promise<GitHubUser> {
  const data = await githubUserGet();
  if (!data.login) {
    throw new Error('GitHub user response missing login.');
  }

  return {
    login: data.login,
    name: data.name ?? null,
    avatar_url: data.avatar_url,
  };
}

export function setGitHubToken(token: string): void {
  sessionStorage.setItem(TOKEN_KEY, token);
  window.dispatchEvent(new CustomEvent('github-auth-change'));
}

export function clearGitHubToken(): void {
  sessionStorage.removeItem(TOKEN_KEY);
  window.dispatchEvent(new CustomEvent('github-auth-change'));
}

export function isGitHubAuthenticated(): boolean {
  return Boolean(getGitHubToken());
}

export function consumeGitHubRedirect(): string | null {
  const redirect = sessionStorage.getItem(REDIRECT_KEY);
  if (redirect) {
    sessionStorage.removeItem(REDIRECT_KEY);
  }
  return redirect;
}
